import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { OrganizationPicker } from '../common';
import {
  Repeat,
  Check,
  X,
  Loader2,
  AlertCircle,
  Building2,
  Search,
  Eye,
  Clock,
  CreditCard,
  FileText,
  Plus,
  ChevronDown,
  ChevronUp,
  Calendar,
  DollarSign,
  Users,
  RefreshCw,
  Ban,
  Play,
  Pause,
  History,
  Package,
} from 'lucide-react';

/**
 * SubscriptionSection - Manages subscriptions in admin billing
 *
 * Features:
 * - View all subscriptions with filtering
 * - Create new subscriptions
 * - Approve/reject direct bill requests
 * - Cancel/reactivate subscriptions
 * - View subscription history
 */
const SubscriptionSection = ({ currentUser }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [processing, setProcessing] = useState(false);

  // Subscriptions data
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionFilter, setSubscriptionFilter] = useState({
    status: '',
    payment_type: '',
  });

  // Pending approvals
  const [pendingApprovals, setPendingApprovals] = useState([]);

  // Selected subscription detail
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);

  // Create subscription modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [createForm, setCreateForm] = useState({
    organization_id: '',
    package_id: '',
    billing_interval: 'monthly',
    yearly_discount_percent: 0,
    promo_type: '',
    promo_duration_days: '',
    promo_discount_percent: '',
    promo_requires_payment_method: true,
    payment_type: 'credit_card',
    payment_terms_days: 30,
    custom_base_price: '',
    auto_renew: true,
    notes: '',
    send_for_approval: false,
    approval_user_id: '',
  });

  // Reference data
  const [organizations, setOrganizations] = useState([]);
  const [packages, setPackages] = useState([]);
  const [eligibleApprovers, setEligibleApprovers] = useState([]);
  const [loadingApprovers, setLoadingApprovers] = useState(false);
  const [orgSearch, setOrgSearch] = useState('');

  // Load subscriptions on mount
  useEffect(() => {
    loadSubscriptions();
    loadPendingApprovals();
    loadOrganizations();
    loadPackages();
  }, []);

  useEffect(() => {
    loadSubscriptions();
  }, [subscriptionFilter]);

  const loadSubscriptions = async () => {
    try {
      setLoading(true);
      const params = {};
      if (subscriptionFilter.status) params.status = subscriptionFilter.status;
      if (subscriptionFilter.payment_type) params.payment_type = subscriptionFilter.payment_type;

      const response = await axios.get(
        `${getApiBase()}/admin/subscriptions`,
        {
          headers: getAuthHeaders(),
          params: { ...params, limit: 100 },
        }
      );
      setSubscriptions(response.data.subscriptions || []);
    } catch (err) {
      console.error('Error loading subscriptions:', err);
      setError('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingApprovals = async () => {
    try {
      const response = await axios.get(
        `${getApiBase()}/admin/subscriptions/pending/internal`,
        { headers: getAuthHeaders() }
      );
      setPendingApprovals(response.data.pending_approvals || []);
    } catch (err) {
      console.error('Error loading pending approvals:', err);
    }
  };

  const loadOrganizations = async () => {
    try {
      setLoadingOrgs(true);
      const response = await axios.get(
        `${getApiBase()}/admin/organizations`,
        { headers: getAuthHeaders() }
      );
      // Admin organizations endpoint returns array directly
      setOrganizations(response.data || []);
    } catch (err) {
      console.error('Error loading organizations:', err);
      setError('Failed to load organizations for dropdown');
    } finally {
      setLoadingOrgs(false);
    }
  };

  const loadPackages = async () => {
    try {
      const response = await axios.get(
        `${getApiBase()}/admin/packages`,
        { headers: getAuthHeaders() }
      );
      // Admin packages endpoint returns array directly
      setPackages(response.data || []);
    } catch (err) {
      console.error('Error loading packages:', err);
    }
  };

  const loadEligibleApprovers = async (orgId) => {
    if (!orgId) {
      setEligibleApprovers([]);
      return;
    }
    try {
      setLoadingApprovers(true);
      const response = await axios.get(
        `${getApiBase()}/admin/organizations/${orgId}/eligible-approvers`,
        { headers: getAuthHeaders() }
      );
      setEligibleApprovers(response.data.approvers || []);
    } catch (err) {
      console.error('Error loading eligible approvers:', err);
      setEligibleApprovers([]);
    } finally {
      setLoadingApprovers(false);
    }
  };

  // Load eligible approvers when organization changes
  useEffect(() => {
    if (createForm.organization_id) {
      loadEligibleApprovers(createForm.organization_id);
      // Reset the selected approver when org changes
      setCreateForm(prev => ({ ...prev, approval_user_id: '' }));
    } else {
      setEligibleApprovers([]);
    }
  }, [createForm.organization_id]);

  const loadSubscriptionDetail = async (subscriptionId) => {
    try {
      const response = await axios.get(
        `${getApiBase()}/admin/subscriptions/${subscriptionId}`,
        { headers: getAuthHeaders() }
      );
      setSelectedSubscription(response.data.subscription);
      setSubscriptionHistory(response.data.subscription.recent_events || []);
    } catch (err) {
      console.error('Error loading subscription detail:', err);
      setError('Failed to load subscription details');
    }
  };

  const createSubscription = async () => {
    try {
      setProcessing(true);
      setError('');

      const payload = {
        ...createForm,
        yearly_discount_percent: parseFloat(createForm.yearly_discount_percent) || 0,
        promo_duration_days: createForm.promo_duration_days ? parseInt(createForm.promo_duration_days) : null,
        promo_discount_percent: createForm.promo_discount_percent ? parseFloat(createForm.promo_discount_percent) : null,
        payment_terms_days: parseInt(createForm.payment_terms_days) || 30,
        custom_base_price: createForm.custom_base_price ? parseFloat(createForm.custom_base_price) : null,
        promo_type: createForm.promo_type || null,
      };

      await axios.post(
        `${getApiBase()}/admin/subscriptions`,
        payload,
        { headers: getAuthHeaders() }
      );

      setSuccess('Subscription created successfully');
      setShowCreateModal(false);
      resetCreateForm();
      loadSubscriptions();
      loadPendingApprovals();
    } catch (err) {
      console.error('Error creating subscription:', err);
      setError(err.response?.data?.detail || 'Failed to create subscription');
    } finally {
      setProcessing(false);
    }
  };

  const approveInternalSubscription = async (subscriptionId, notes = '') => {
    try {
      setProcessing(true);
      await axios.post(
        `${getApiBase()}/admin/subscriptions/${subscriptionId}/approve-internal`,
        { notes },
        { headers: getAuthHeaders() }
      );
      setSuccess('Subscription approved');
      loadSubscriptions();
      loadPendingApprovals();
      setSelectedSubscription(null);
    } catch (err) {
      console.error('Error approving subscription:', err);
      setError(err.response?.data?.detail || 'Failed to approve subscription');
    } finally {
      setProcessing(false);
    }
  };

  const rejectInternalSubscription = async (subscriptionId, reason) => {
    if (!reason) {
      setError('Rejection reason is required');
      return;
    }
    try {
      setProcessing(true);
      await axios.post(
        `${getApiBase()}/admin/subscriptions/${subscriptionId}/reject-internal`,
        { reason },
        { headers: getAuthHeaders() }
      );
      setSuccess('Subscription rejected');
      loadSubscriptions();
      loadPendingApprovals();
      setSelectedSubscription(null);
    } catch (err) {
      console.error('Error rejecting subscription:', err);
      setError(err.response?.data?.detail || 'Failed to reject subscription');
    } finally {
      setProcessing(false);
    }
  };

  const cancelSubscription = async (subscriptionId, reason, immediately = false) => {
    try {
      setProcessing(true);
      await axios.post(
        `${getApiBase()}/admin/subscriptions/${subscriptionId}/cancel`,
        { reason, cancel_immediately: immediately },
        { headers: getAuthHeaders() }
      );
      setSuccess(`Subscription ${immediately ? 'cancelled' : 'scheduled for cancellation'}`);
      loadSubscriptions();
      setSelectedSubscription(null);
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      setError(err.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setProcessing(false);
    }
  };

  const reactivateSubscription = async (subscriptionId) => {
    try {
      setProcessing(true);
      await axios.post(
        `${getApiBase()}/admin/subscriptions/${subscriptionId}/reactivate`,
        {},
        { headers: getAuthHeaders() }
      );
      setSuccess('Subscription reactivated');
      loadSubscriptions();
      setSelectedSubscription(null);
    } catch (err) {
      console.error('Error reactivating subscription:', err);
      setError(err.response?.data?.detail || 'Failed to reactivate subscription');
    } finally {
      setProcessing(false);
    }
  };

  const resetCreateForm = () => {
    setCreateForm({
      organization_id: '',
      package_id: '',
      billing_interval: 'monthly',
      yearly_discount_percent: 0,
      promo_type: '',
      promo_duration_days: '',
      promo_discount_percent: '',
      promo_requires_payment_method: true,
      payment_type: 'credit_card',
      payment_terms_days: 30,
      custom_base_price: '',
      auto_renew: true,
      notes: '',
      send_for_approval: false,
      approval_user_id: '',
    });
    setEligibleApprovers([]);
  };

  const openCreateModal = () => {
    setShowCreateModal(true);
    // Reload organizations when opening the modal to ensure fresh data
    if (organizations.length === 0) {
      loadOrganizations();
    }
    if (packages.length === 0) {
      loadPackages();
    }
  };

  const formatCurrency = (cents) => {
    if (!cents && cents !== 0) return '-';
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      active: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      pending_client_approval: 'bg-blue-100 text-blue-800',
      pending_internal_approval: 'bg-orange-100 text-orange-800',
      cancelled: 'bg-gray-100 text-gray-800',
      expired: 'bg-red-100 text-red-800',
    };
    const style = statusStyles[status] || 'bg-gray-100 text-gray-800';
    const label = status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown';
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${style}`}>
        {label}
      </span>
    );
  };

  const getPaymentTypeBadge = (paymentType) => {
    if (paymentType === 'credit_card') {
      return (
        <span className="flex items-center text-sm text-gray-600">
          <CreditCard className="w-4 h-4 mr-1" />
          Card
        </span>
      );
    }
    if (paymentType === 'ach') {
      return (
        <span className="flex items-center text-sm text-blue-600">
          <Building2 className="w-4 h-4 mr-1" />
          ACH
        </span>
      );
    }
    return (
      <span className="flex items-center text-sm text-gray-600">
        <FileText className="w-4 h-4 mr-1" />
        Invoice
      </span>
    );
  };

  // Filter organizations for dropdown
  const filteredOrganizations = organizations.filter(
    org => !orgSearch || org.name?.toLowerCase().includes(orgSearch.toLowerCase())
  );

  return (
    <div className="bg-gray-50 min-h-screen space-y-6">
      {/* Alerts */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-800">
          <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-red-600 hover:text-red-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center text-green-800">
          <Check className="w-5 h-5 mr-2 flex-shrink-0" />
          <span>{success}</span>
          <button onClick={() => setSuccess('')} className="ml-auto text-green-600 hover:text-green-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Pending Approvals Alert */}
      {pendingApprovals.length > 0 && (
        <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="flex items-center">
            <Clock className="w-5 h-5 mr-2 text-orange-600" />
            <span className="text-orange-800 font-medium">
              {pendingApprovals.length} subscription(s) pending internal approval
            </span>
          </div>
        </div>
      )}

      {/* Header with Actions */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Subscriptions</h2>
          <p className="text-sm text-gray-500">Manage organization subscriptions</p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Subscription
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 bg-gray-50 p-4 rounded-lg">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
          <select
            value={subscriptionFilter.status}
            onChange={(e) => setSubscriptionFilter({ ...subscriptionFilter, status: e.target.value })}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="pending_client_approval">Pending Client Approval</option>
            <option value="pending_internal_approval">Pending Internal Approval</option>
            <option value="cancelled">Cancelled</option>
            <option value="expired">Expired</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Payment Type</label>
          <select
            value={subscriptionFilter.payment_type}
            onChange={(e) => setSubscriptionFilter({ ...subscriptionFilter, payment_type: e.target.value })}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="">All Types</option>
            <option value="credit_card">Credit Card</option>
            <option value="direct_bill">Direct Bill</option>
            <option value="ach">ACH (Bank Transfer)</option>
          </select>
        </div>
        <div className="ml-auto flex items-end">
          <button
            onClick={() => { loadSubscriptions(); loadPendingApprovals(); }}
            className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-800"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </button>
        </div>
      </div>

      {/* Subscriptions Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : subscriptions.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No subscriptions found
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Package
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Billing
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Next Billing
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {subscriptions.map((sub) => (
                <tr key={sub.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Building2 className="w-5 h-5 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-gray-900">
                        {sub.organization?.name || sub.organization_id}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{sub.package_name}</div>
                    <div className="text-sm text-gray-500">
                      {formatCurrency(sub.effective_price)} / {sub.billing_interval}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(sub.status)}
                    {sub.cancel_at_period_end && (
                      <span className="ml-2 text-xs text-red-600">(Cancelling)</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {sub.billing_interval?.charAt(0).toUpperCase() + sub.billing_interval?.slice(1) || '-'}
                    {sub.yearly_discount_percent > 0 && (
                      <span className="ml-1 text-green-600">(-{sub.yearly_discount_percent}%)</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getPaymentTypeBadge(sub.payment_type)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(sub.next_billing_date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => loadSubscriptionDetail(sub.id)}
                      className="text-blue-600 hover:text-blue-800 mr-3"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Subscription Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-[70] overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-30" onClick={() => setShowCreateModal(false)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b flex justify-between items-center sticky top-0 bg-white z-10">
                <h2 className="text-xl font-semibold text-gray-900">Create Subscription</h2>
                <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Organization Selection */}
                <div>
                  <OrganizationPicker
                    organizations={organizations}
                    value={createForm.organization_id}
                    onChange={(orgId) => setCreateForm({ ...createForm, organization_id: orgId })}
                    placeholder="Select organization..."
                    label="Organization"
                    required
                    loading={loadingOrgs}
                    showDomain
                    showUserCount
                  />
                  {organizations.length === 0 && !loadingOrgs && (
                    <button
                      type="button"
                      onClick={loadOrganizations}
                      className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                    >
                      Retry loading organizations
                    </button>
                  )}
                </div>

                {/* Package Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Package</label>
                  <select
                    value={createForm.package_id}
                    onChange={(e) => setCreateForm({ ...createForm, package_id: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  >
                    <option value="">Select package...</option>
                    {packages.map((pkg) => (
                      <option key={pkg.id} value={pkg.id}>
                        {pkg.name} - ${pkg.base_price}/mo
                      </option>
                    ))}
                  </select>
                </div>

                {/* Billing Configuration */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Billing Interval</label>
                  <select
                    value={createForm.billing_interval}
                    onChange={(e) => setCreateForm({ ...createForm, billing_interval: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>

                {createForm.billing_interval === 'yearly' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Yearly Discount (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={createForm.yearly_discount_percent}
                      onChange={(e) => setCreateForm({ ...createForm, yearly_discount_percent: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                )}

                {/* Payment Configuration */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Type</label>
                    <select
                      value={createForm.payment_type}
                      onChange={(e) => setCreateForm({ ...createForm, payment_type: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="credit_card">Credit Card</option>
                      <option value="direct_bill">Direct Bill (Invoice)</option>
                      <option value="ach">ACH (Bank Transfer via Stripe)</option>
                    </select>
                  </div>
                  {createForm.payment_type === 'direct_bill' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms (days)</label>
                      <input
                        type="number"
                        min="1"
                        value={createForm.payment_terms_days}
                        onChange={(e) => setCreateForm({ ...createForm, payment_terms_days: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                  )}
                  {createForm.payment_type === 'ach' && (
                    <div>
                      <p className="text-sm text-blue-600 mt-1">
                        <Building2 className="w-4 h-4 inline mr-1" />
                        ACH payments will be processed via Stripe. The organization must have a bank account configured in Billing &amp; Finance settings before charges can be collected.
                      </p>
                    </div>
                  )}
                </div>

                {/* Promo Configuration */}
                <div className="border-t pt-4">
                  <h3 className="font-medium text-gray-900 mb-3">Promotional Period (Optional)</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Promo Type</label>
                      <select
                        value={createForm.promo_type}
                        onChange={(e) => setCreateForm({ ...createForm, promo_type: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        <option value="">None</option>
                        <option value="free_trial">Free Trial</option>
                        <option value="discounted">Discounted Period</option>
                      </select>
                    </div>
                    {createForm.promo_type && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Duration (days)</label>
                        <input
                          type="number"
                          min="1"
                          value={createForm.promo_duration_days}
                          onChange={(e) => setCreateForm({ ...createForm, promo_duration_days: e.target.value })}
                          className="w-full px-3 py-2 border rounded-lg"
                        />
                      </div>
                    )}
                  </div>
                  {createForm.promo_type === 'discounted' && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Discount (%)</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={createForm.promo_discount_percent}
                        onChange={(e) => setCreateForm({ ...createForm, promo_discount_percent: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                  )}
                  {createForm.promo_type && (
                    <div className="mt-4 flex items-center">
                      <input
                        type="checkbox"
                        id="promo_requires_payment"
                        checked={createForm.promo_requires_payment_method}
                        onChange={(e) => setCreateForm({ ...createForm, promo_requires_payment_method: e.target.checked })}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      />
                      <label htmlFor="promo_requires_payment" className="ml-2 text-sm text-gray-700">
                        Require payment method for trial
                      </label>
                    </div>
                  )}
                </div>

                {/* Custom Pricing */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Custom Base Price (optional, in dollars)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={createForm.custom_base_price}
                    onChange={(e) => setCreateForm({ ...createForm, custom_base_price: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Leave empty to use package price"
                  />
                </div>

                {/* Client Approval */}
                <div className="border-t pt-4">
                  <div className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      id="send_for_approval"
                      checked={createForm.send_for_approval}
                      onChange={(e) => setCreateForm({ ...createForm, send_for_approval: e.target.checked })}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    <label htmlFor="send_for_approval" className="ml-2 text-sm font-medium text-gray-700">
                      Send for client approval
                    </label>
                  </div>
                  {createForm.send_for_approval && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Send Approval To</label>
                      {!createForm.organization_id ? (
                        <p className="text-sm text-gray-500 italic">Select an organization first</p>
                      ) : loadingApprovers ? (
                        <div className="flex items-center text-sm text-gray-500">
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Loading eligible approvers...
                        </div>
                      ) : eligibleApprovers.length === 0 ? (
                        <p className="text-sm text-amber-600">
                          No eligible approvers found. Users must be an organization admin or have the Billing Subscription Approve permission.
                        </p>
                      ) : (
                        <select
                          value={createForm.approval_user_id}
                          onChange={(e) => setCreateForm({ ...createForm, approval_user_id: e.target.value })}
                          className="w-full px-3 py-2 border rounded-lg"
                        >
                          <option value="">Select approver...</option>
                          {eligibleApprovers.map(user => (
                            <option key={user.id} value={user.id}>
                              {user.name} ({user.email}){user.is_org_admin ? ' - Org Admin' : ''}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={createForm.notes}
                    onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    rows="2"
                    placeholder="Internal notes about this subscription..."
                  />
                </div>
              </div>

              <div className="px-6 py-4 border-t flex justify-end space-x-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={createSubscription}
                  disabled={processing || !createForm.organization_id || !createForm.package_id || (createForm.send_for_approval && !createForm.approval_user_id)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Create Subscription
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Detail Modal */}
      {selectedSubscription && (
        <div className="fixed inset-0 z-[70] overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-30" onClick={() => setSelectedSubscription(null)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b flex justify-between items-center sticky top-0 bg-white z-10">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Subscription Details</h2>
                  <p className="text-sm text-gray-500">{selectedSubscription.organization?.name}</p>
                </div>
                <button onClick={() => setSelectedSubscription(null)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Status & Actions */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {getStatusBadge(selectedSubscription.status)}
                    {selectedSubscription.cancel_at_period_end && (
                      <span className="text-sm text-red-600">Scheduled for cancellation</span>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <Link
                      to={`/admin/entitlements/${selectedSubscription.organization_id}`}
                      className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded hover:bg-purple-200 flex items-center"
                    >
                      <Package className="w-4 h-4 mr-1" />
                      View Entitlements
                    </Link>
                    {selectedSubscription.status === 'pending_internal_approval' && (
                      <>
                        <button
                          onClick={() => approveInternalSubscription(selectedSubscription.id)}
                          disabled={processing}
                          className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 flex items-center"
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Approve
                        </button>
                        <button
                          onClick={() => {
                            const reason = prompt('Enter rejection reason:');
                            if (reason) rejectInternalSubscription(selectedSubscription.id, reason);
                          }}
                          disabled={processing}
                          className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 flex items-center"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Reject
                        </button>
                      </>
                    )}
                    {selectedSubscription.status === 'active' && !selectedSubscription.cancel_at_period_end && (
                      <button
                        onClick={() => {
                          const reason = prompt('Enter cancellation reason (optional):');
                          cancelSubscription(selectedSubscription.id, reason, false);
                        }}
                        disabled={processing}
                        className="px-3 py-1 bg-red-100 text-red-700 text-sm rounded hover:bg-red-200 flex items-center"
                      >
                        <Ban className="w-4 h-4 mr-1" />
                        Cancel
                      </button>
                    )}
                    {selectedSubscription.status === 'active' && selectedSubscription.cancel_at_period_end && (
                      <button
                        onClick={() => reactivateSubscription(selectedSubscription.id)}
                        disabled={processing}
                        className="px-3 py-1 bg-green-100 text-green-700 text-sm rounded hover:bg-green-200 flex items-center"
                      >
                        <Play className="w-4 h-4 mr-1" />
                        Reactivate
                      </button>
                    )}
                  </div>
                </div>

                {/* Subscription Info Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-500">Package</div>
                    <div className="font-medium">{selectedSubscription.package_name}</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-500">Effective Price</div>
                    <div className="font-medium">
                      {formatCurrency(selectedSubscription.effective_price)} / {selectedSubscription.billing_interval}
                    </div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-500">Payment Type</div>
                    <div className="font-medium flex items-center">
                      {getPaymentTypeBadge(selectedSubscription.payment_type)}
                    </div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-500">Next Billing Date</div>
                    <div className="font-medium">{formatDate(selectedSubscription.next_billing_date)}</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-500">Current Period</div>
                    <div className="font-medium">
                      {formatDate(selectedSubscription.current_period_start)} - {formatDate(selectedSubscription.current_period_end)}
                    </div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-500">Auto Renew</div>
                    <div className="font-medium">
                      {selectedSubscription.auto_renew ? (
                        <span className="text-green-600 flex items-center">
                          <Check className="w-4 h-4 mr-1" /> Yes
                        </span>
                      ) : (
                        <span className="text-gray-600 flex items-center">
                          <X className="w-4 h-4 mr-1" /> No
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Credit Balance (for troubleshooting proration) */}
                {selectedSubscription.credit_balance && selectedSubscription.status === 'active' && (
                  <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                    <h3 className="font-medium text-green-900 mb-2 flex items-center">
                      <DollarSign className="w-4 h-4 mr-1" />
                      Current Credit Balance (Prorated)
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-green-700">Credit Amount:</span>{' '}
                        <span className="font-semibold text-green-900">
                          {formatCurrency(selectedSubscription.credit_balance.credit_amount)}
                        </span>
                      </div>
                      <div>
                        <span className="text-green-700">Days Remaining:</span>{' '}
                        <span className="font-medium">
                          {selectedSubscription.credit_balance.days_remaining} / {selectedSubscription.credit_balance.total_days}
                        </span>
                      </div>
                      <div>
                        <span className="text-green-700">Daily Rate:</span>{' '}
                        <span className="font-medium">
                          {formatCurrency(selectedSubscription.credit_balance.daily_rate)}
                        </span>
                      </div>
                      <div>
                        <span className="text-green-700">Days Used:</span>{' '}
                        <span className="font-medium">
                          {selectedSubscription.credit_balance.days_used}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-green-600 mt-2">
                      This is the prorated credit that would be applied if upgraded or cancelled today.
                    </p>
                  </div>
                )}

                {/* Promo Info */}
                {selectedSubscription.promo_type && (
                  <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                    <h3 className="font-medium text-blue-900 mb-2">Promotional Period</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-blue-700">Type:</span>{' '}
                        {selectedSubscription.promo_type === 'free_trial' ? 'Free Trial' : 'Discounted'}
                      </div>
                      <div>
                        <span className="text-blue-700">Duration:</span>{' '}
                        {selectedSubscription.promo_duration_days} days
                      </div>
                      <div>
                        <span className="text-blue-700">Start:</span>{' '}
                        {formatDate(selectedSubscription.promo_start_date)}
                      </div>
                      <div>
                        <span className="text-blue-700">End:</span>{' '}
                        {formatDate(selectedSubscription.promo_end_date)}
                      </div>
                    </div>
                  </div>
                )}

                {/* Notes */}
                {selectedSubscription.notes && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Notes</h3>
                    <p className="text-gray-900">{selectedSubscription.notes}</p>
                  </div>
                )}

                {/* Recent History */}
                {subscriptionHistory.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center">
                      <History className="w-4 h-4 mr-1" />
                      Recent Events
                    </h3>
                    <div className="space-y-2">
                      {subscriptionHistory.slice(0, 10).map((event) => (
                        <div
                          key={event.id}
                          className="flex items-center justify-between text-sm py-2 border-b last:border-0"
                        >
                          <div>
                            <span className="font-medium text-gray-900">
                              {event.event_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </span>
                            {event.actor_name && (
                              <span className="text-gray-500 ml-2">by {event.actor_name}</span>
                            )}
                            {event.is_system_event && (
                              <span className="text-gray-400 ml-2">(system)</span>
                            )}
                          </div>
                          <div className="text-gray-500">{formatDate(event.timestamp)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionSection;
